import React, { useState, useCallback, useEffect } from 'react';
import { $getSelection, $isTextNode, FORMAT_TEXT_COMMAND } from 'lexical';
import { $patchStyleText } from '@lexical/selection';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/free-solid-svg-icons';

const CustomColorPicker2 = ({ fieldState, setFormState }) => {
  // Single state for color management
  const [currentColor, setCurrentColor] = useState("#000000");

  const presetColors = [
    "#000000", // Black
    "#FF0000", // Red
    "#800080", // Purple
    "#0000FF", // Blue
    "#008000", // Green
  ];

  // Extract color from style string
  const extractColorFromStyle = useCallback((style) => {
    if (!style) return "#000000";
    const colorMatch = style.match(/color:\s*([^;]+)/);
    return colorMatch ? colorMatch[1].trim() : "#000000";
  }, []);

  // Apply color to selection or cursor position
  const applyColorToSelection = useCallback((color) => {
    try {
      const activeEditor = fieldState?.editor;
      
      if (!activeEditor || !fieldState) {
        
        return;
      }      
      setCurrentColor(color);
      
      activeEditor.update(() => {
        const selection = $getSelection();
        if (!selection) return;

        if (!selection.isCollapsed()) {
          // Apply to selected text
          $patchStyleText(selection, { color: color });
        } else {
          // For cursor position, apply style to the selection itself
          // This will affect future typing without modifying existing text
          $patchStyleText(selection, { color: color });
        }
      });
      
    } catch (error) {
      console.error('Error applying color:', error);
    }
  }, [fieldState]);

  // Detect current color from selection or cursor position
  const detectCurrentColor = useCallback(() => {
    const activeEditor = fieldState?.editor;
    if (!activeEditor) return;

    activeEditor.getEditorState().read(() => {
      const selection = $getSelection();
      if (!selection) return;

      let detectedColor = "#000000";
      
      try {
        if (!selection.isCollapsed()) {
          // Get color from selected text
          const nodes = selection.getNodes();
          if (nodes.length > 0) {
            const firstNode = nodes[0];
            if ($isTextNode(firstNode)) {
              detectedColor = extractColorFromStyle(firstNode.getStyle());
            }
          }
        } else {
          // Get color from cursor position
          const anchor = selection.anchor;
          if (anchor) {
            const node = anchor.getNode();
            if (node && $isTextNode(node)) {
              detectedColor = extractColorFromStyle(node.getStyle());
            }
          }
        }
        
        setCurrentColor(detectedColor);
      } catch (error) {
        console.error('Error detecting color:', error);
      }
    });
  }, [fieldState, extractColorFromStyle]);

  // Listen for editor updates to detect color changes
  useEffect(() => {
    if (!fieldState?.editor) return;

    const unregister = fieldState.editor.registerUpdateListener(() => {
      detectCurrentColor();
    });

    // Initial color detection
    detectCurrentColor();

    return unregister;
  }, [fieldState, detectCurrentColor]);

  // Handle color input change
  const handleColorInputChange = useCallback((e) => {
    const newColor = e.target.value;
    applyColorToSelection(newColor);
  }, [applyColorToSelection]);

  // Handle preset color click
  const handlePresetColorClick = useCallback((event, color) => {
    event.preventDefault();
    applyColorToSelection(color);
  }, [applyColorToSelection]);

  // Handle keyboard navigation for preset colors
  const handlePresetKeyDown = useCallback((event, color) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      applyColorToSelection(color);
    }
  }, [applyColorToSelection]);

  return (
    <div className="color-picker-container" style={{ marginLeft: "120px" }}>
      <div
        className="custom-color-picker"
        style={{ padding: "0px !important" }}
      >
        <div className="color-picker-content">
          <div className="color-picker-left">
            <span
              className="edit-icon"
              style={{ display: "inline-block", borderBottom: "1px solid" }}
              onClick={ ()=> fieldState?.editor?.dispatchCommand(FORMAT_TEXT_COMMAND, 'highlight')
              }
            >
              <FontAwesomeIcon
                icon={faPencil}
                style={{ fontSize: "smaller" }}
              />
            </span>

            <div className="color-options">
              {presetColors.map((color) => (
                <div
                  key={color}
                  className={`color-swatch ${
                    currentColor === color ? "selected" : ""
                  }`}
                  style={{ 
                    backgroundColor: color,
                    border: currentColor === color ? '2px solid #007bff' : '1px solid #ccc'
                  }}
                  onMouseDown={(event) => handlePresetColorClick(event, color)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Select color ${color}`}
                  onKeyDown={(event) => handlePresetKeyDown(event, color)}
                />
              ))}
            </div>
          </div>
          
          <div className="hex-input-container">
            <input
              type="color"
              value={currentColor}
              onChange={handleColorInputChange}
              className="hex-input"
              aria-label="Custom color picker"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomColorPicker2;